import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App';
import ErrorBoundary from './components/common/ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './utils/serviceWorkerRegistration';

// Web Vitals için analytics gönderimi
const sendToAnalytics = (metric) => {
  // Google Analytics 4'e gönder
  if (window.gtag) {
    window.gtag('event', metric.name, {
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      event_category: 'Web Vitals',
      event_label: metric.id,
      non_interaction: true,
    });
  }

  // Console'a da log et (development)
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web Vitals] ${metric.name}:`, metric.value);
  }
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// Hide the loading overlay once React has rendered
const hideLoader = () => {
  const loader = document.getElementById('app-loader');
  const rootEl = document.getElementById('root');

  if (loader) {
    loader.classList.add('fade-out');
    setTimeout(() => {
      loader.style.display = 'none';
    }, 300);
  }

  if (rootEl) {
    rootEl.classList.add('loaded');
  }
};

// Wait for all stylesheets to be loaded
const waitForStyles = () => {
  return new Promise((resolve) => {
    // Check if all link stylesheets are loaded
    const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
    let loadedCount = 0;
    const totalCount = stylesheets.length;

    if (totalCount === 0) {
      resolve();
      return;
    }

    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount >= totalCount) {
        resolve();
      }
    };

    stylesheets.forEach((link) => {
      if (link.sheet) {
        // Already loaded
        checkLoaded();
      } else {
        link.addEventListener('load', checkLoaded);
        link.addEventListener('error', checkLoaded); // Continue even on error
      }
    });

    // Fallback timeout - don't wait forever
    setTimeout(resolve, 2000);
  });
};

// Wait for fonts, styles, and a frame to be ready, then hide loader
Promise.all([
  document.fonts?.ready || Promise.resolve(),
  waitForStyles(),
  new Promise(resolve => setTimeout(resolve, 100)) // Minimum delay
]).then(() => {
  requestAnimationFrame(() => {
    requestAnimationFrame(hideLoader);
  });
});

// Web Vitals ölçümlerini aktif et
reportWebVitals(sendToAnalytics);

// PWA Service Worker devre dışı: cache-first davranış bazen ilk açılışta eski CSS/JS karışmasına
// neden olup sayfayı bozuk gösterebiliyor. Prod'da SW'yi kaldırıp tek seferlik reload ile kontrolü bırakıyoruz.
if (process.env.NODE_ENV === 'production') {
  try {
    const didReloadKey = 'sw-unregister-reloaded-v1';
    const shouldReloadOnce = typeof window !== 'undefined' && window.sessionStorage && !window.sessionStorage.getItem(didReloadKey);

    serviceWorkerRegistration.unregister();

    // Also clear Cache Storage to avoid stale assets sticking around.
    if (typeof window !== 'undefined' && 'caches' in window) {
      window.caches.keys().then((names) => {
        return Promise.all(
          names
            .filter((name) => name.startsWith('order-panel-') || name.startsWith('workbox-') || name.startsWith('sw-'))
            .map((name) => window.caches.delete(name))
        );
      }).catch(() => {
        // ignore
      });
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        return Promise.all(regs.map((r) => r.unregister()));
      }).then(() => {
        if (shouldReloadOnce) {
          window.sessionStorage.setItem(didReloadKey, '1');
          window.location.reload();
        }
      }).catch(() => {
        // ignore
      });
    }
  } catch (e) {
    // ignore
  }
}
